import type Highcharts from 'highcharts';
import { DEFAULT_THEME } from '../defaults/theme.defaults';
import {
	applyChartLegendScale,
	hideHighchartsLegendDom,
	isHighchartsChartAlive,
	isHighchartsLegendEnabled,
	rerenderHighchartsLegend,
} from './chart-legend-scale.util';

const AXIS_COLOR_KEYS = [
	'gridLineColor',
	'minorGridLineColor',
	'lineColor',
	'tickColor',
] as const;

type AxisColorKey = (typeof AXIS_COLOR_KEYS)[number];

type AxisOptions = Highcharts.XAxisOptions | Highcharts.YAxisOptions;

const CSS_VAR_PATTERN = /var\(\s*(--[^,\s)]+)\s*(?:,\s*([^)]+))?\)/g;

/** Reads a numeric CSS custom property from `:root` (e.g. `1px` → 1). */
export function resolveCssNumber(cssVarName: string, fallback: number): number {
	if (typeof window === 'undefined') {
		return fallback;
	}
	const rawValue = getComputedStyle(document.documentElement)
		.getPropertyValue(cssVarName)
		.trim();
	const numericValue = Number.parseFloat(rawValue.replace('px', ''));
	return Number.isFinite(numericValue) ? numericValue : fallback;
}

const CSS_VAR_THEME_FALLBACKS: Record<string, { light: string; dark: string }> =
	{
		'--border-secondary-color': {
			light: DEFAULT_THEME.colors.light.border.secondary,
			dark: DEFAULT_THEME.colors.dark.border.secondary,
		},
		'--border-primary-color': {
			light: DEFAULT_THEME.colors.light.border.primary,
			dark: DEFAULT_THEME.colors.dark.border.primary,
		},
	};

const GRID_LINE_COLOR_KEYS = new Set<AxisColorKey>([
	'gridLineColor',
	'minorGridLineColor',
]);

function parseColorChannels(
	color: string
): { r: number; g: number; b: number; a: number } | null {
	const trimmed = color.trim();
	const hexMatch = trimmed.match(/^#([0-9a-fA-F]{3,8})$/);
	if (hexMatch) {
		let hex = hexMatch[1];
		if (hex.length === 3) {
			hex = hex
				.split('')
				.map(ch => ch + ch)
				.join('');
		}
		if (hex.length === 6) {
			return {
				r: Number.parseInt(hex.slice(0, 2), 16),
				g: Number.parseInt(hex.slice(2, 4), 16),
				b: Number.parseInt(hex.slice(4, 6), 16),
				a: 1,
			};
		}
		if (hex.length === 8) {
			return {
				r: Number.parseInt(hex.slice(0, 2), 16),
				g: Number.parseInt(hex.slice(2, 4), 16),
				b: Number.parseInt(hex.slice(4, 6), 16),
				a: Number.parseInt(hex.slice(6, 8), 16) / 255,
			};
		}
	}

	const rgbaMatch = trimmed.match(
		/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)$/
	);
	if (rgbaMatch) {
		return {
			r: Number(rgbaMatch[1]),
			g: Number(rgbaMatch[2]),
			b: Number(rgbaMatch[3]),
			a: rgbaMatch[4] !== undefined ? Number(rgbaMatch[4]) : 1,
		};
	}

	return null;
}

function extractFirstCssVarName(value: string): string | undefined {
	const match = value.match(/var\(\s*(--[^,\s)]+)/);
	return match?.[1];
}

function isInvisibleChartStrokeColor(
	color: string,
	host?: HTMLElement | null
): boolean {
	const channels = parseColorChannels(color);
	if (!channels) {
		return false;
	}
	if (channels.a < 0.1) {
		return true;
	}

	if (!host || typeof document === 'undefined') {
		return false;
	}

	const cardBg = readCssVar(
		getComputedStyle(host),
		'--card-background-color'
	);
	const bgChannels = cardBg ? parseColorChannels(cardBg) : null;
	if (!bgChannels) {
		return false;
	}

	return (
		channels.r === bgChannels.r &&
		channels.g === bgChannels.g &&
		channels.b === bgChannels.b
	);
}

function visibleGridLineFallback(
	rawValue: string,
	host: HTMLElement | null | undefined
): string {
	const themeMode = inferThemeMode(host);
	const cssVarName =
		extractFirstCssVarName(rawValue) ?? '--border-secondary-color';
	const fallback = CSS_VAR_THEME_FALLBACKS[cssVarName];
	if (fallback) {
		return fallback[themeMode];
	}
	return CSS_VAR_THEME_FALLBACKS['--border-secondary-color'][themeMode];
}

function readCssVar(styles: CSSStyleDeclaration, cssVarName: string): string {
	return styles.getPropertyValue(cssVarName).trim();
}

function inferThemeMode(element?: HTMLElement | null): 'light' | 'dark' {
	if (typeof document === 'undefined') {
		return 'light';
	}

	const sources = [element, document.documentElement].filter(
		(el): el is HTMLElement => !!el
	);

	for (const el of sources) {
		const bg = readCssVar(getComputedStyle(el), '--card-background-color');
		if (!bg.startsWith('#') || bg.length < 7) {
			continue;
		}
		const r = Number.parseInt(bg.slice(1, 3), 16);
		const g = Number.parseInt(bg.slice(3, 5), 16);
		const b = Number.parseInt(bg.slice(5, 7), 16);
		const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
		if (luminance < 128) {
			return 'dark';
		}
		if (luminance >= 128) {
			return 'light';
		}
	}

	return 'light';
}

export function resolveCssColorToken(
	value: string,
	element?: HTMLElement | null
): string {
	if (!value.includes('var(')) {
		return value;
	}
	if (typeof document === 'undefined') {
		return value;
	}

	const rootStyles = getComputedStyle(document.documentElement);
	const hostStyles = element ? getComputedStyle(element) : null;
	const themeMode = inferThemeMode(element);

	return value.replace(
		CSS_VAR_PATTERN,
		(_match, cssVarName: string, fallback?: string) => {
			let computed = hostStyles ? readCssVar(hostStyles, cssVarName) : '';
			if (!computed) {
				computed = readCssVar(rootStyles, cssVarName);
			}
			if (computed) {
				return computed;
			}

			const trimmedFallback = (fallback ?? '').trim();
			if (trimmedFallback) {
				return trimmedFallback;
			}

			const themeFallback = CSS_VAR_THEME_FALLBACKS[cssVarName];
			if (themeFallback) {
				return themeFallback[themeMode];
			}

			return _match;
		}
	);
}

function resolveAxisColors(
	axis: AxisOptions,
	host: HTMLElement | null | undefined
): AxisOptions {
	const next: AxisOptions = { ...axis };

	for (const key of AXIS_COLOR_KEYS) {
		const raw = next[key as AxisColorKey];
		if (typeof raw !== 'string') {
			continue;
		}

		let resolved = resolveCssColorToken(raw, host);
		if (
			GRID_LINE_COLOR_KEYS.has(key) &&
			isInvisibleChartStrokeColor(resolved, host)
		) {
			resolved = visibleGridLineFallback(raw, host);
		}

		(next as Record<AxisColorKey, string>)[key] = resolved;
	}

	const crosshair = next.crosshair;
	if (
		crosshair &&
		typeof crosshair === 'object' &&
		!Array.isArray(crosshair)
	) {
		const color = (crosshair as { color?: string }).color;
		if (typeof color === 'string') {
			next.crosshair = {
				...crosshair,
				color: resolveCssColorToken(color, host),
			};
		}
	}

	return next;
}

function resolveXAxisOption(
	axis: Highcharts.XAxisOptions | Highcharts.XAxisOptions[] | undefined,
	host: HTMLElement | null | undefined
): Highcharts.XAxisOptions | Highcharts.XAxisOptions[] | undefined {
	if (!axis) {
		return axis;
	}
	if (Array.isArray(axis)) {
		return axis.map(
			entry => resolveAxisColors(entry, host) as Highcharts.XAxisOptions
		);
	}
	return resolveAxisColors(axis, host) as Highcharts.XAxisOptions;
}

function resolveYAxisOption(
	axis: Highcharts.YAxisOptions | Highcharts.YAxisOptions[] | undefined,
	host: HTMLElement | null | undefined
): Highcharts.YAxisOptions | Highcharts.YAxisOptions[] | undefined {
	if (!axis) {
		return axis;
	}
	if (Array.isArray(axis)) {
		return axis.map(
			entry => resolveAxisColors(entry, host) as Highcharts.YAxisOptions
		);
	}
	return resolveAxisColors(axis, host) as Highcharts.YAxisOptions;
}

export function resolveHighchartsCssColors(
	options: Highcharts.Options,
	host?: HTMLElement | null
): Highcharts.Options {
	if (typeof document === 'undefined') {
		return options;
	}

	return {
		...options,
		xAxis: resolveXAxisOption(options.xAxis, host),
		yAxis: resolveYAxisOption(options.yAxis, host),
	};
}

/** True when the chart instance is still mounted in the DOM (Highcharts 13+ uses `container`). */
function isHighchartsChartConnected(
	chart: Highcharts.Chart | undefined
): boolean {
	return chart?.container?.isConnected === true;
}

/** Skip reflow when the chart was destroyed or its container was removed from the DOM. */
export function safeHighchartsReflow(
	chart: Highcharts.Chart | undefined
): void {
	if (isHighchartsChartConnected(chart)) {
		chart.reflow();
	}
}

/** Resize Highcharts to its DOM container (flex/card layouts); falls back to reflow. */
export function reflowHighchartsToContainer(
	chart: Highcharts.Chart | undefined,
	container: HTMLElement | null | undefined
): void {
	if (!isHighchartsChartAlive(chart)) {
		if (container) {
			applyChartLegendScale(container);
		}
		return;
	}

	applyChartLegendScale(container, chart);

	if (!container) {
		safeHighchartsReflow(chart);
		return;
	}

	const { clientWidth, clientHeight } = container;
	if (clientWidth > 0 && clientHeight > 0) {
		safeHighchartsReflow(chart);
		if (isHighchartsLegendEnabled(chart)) {
			rerenderHighchartsLegend(chart);
		} else {
			hideHighchartsLegendDom(chart);
		}
		return;
	}

	safeHighchartsReflow(chart);
}

/**
 * Double-rAF then reflow so flex/modal layout can settle before Highcharts locks size.
 * Fixes editor Card Preview painting at ~half height on first metric, then correcting
 * only after a later config-driven remount (e.g. adding a blank second metric).
 */
export function scheduleHighchartsReflowAfterLayout(
	getChart: () => Highcharts.Chart | undefined,
	container: HTMLElement | null | undefined
): void {
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			const chart = getChart();
			if (!isHighchartsChartAlive(chart)) {
				return;
			}
			reflowHighchartsToContainer(chart, container);
		});
	});
}

/**
 * Observes the plot container and its GridStack card boundary so Highcharts reflows
 * when the card is resized.
 *
 * During an active drag-resize the observer fires continuously. To avoid DOM mutations
 * (legend re-render, chart.update) that can interrupt jQuery UI's mouse tracking, the
 * observer only calls chart.reflow() — a safe SVG dimension update with no node changes.
 * The full reflow (legend scale, chart.update) is deferred to resizestop via
 * reflowHighchartsPlotsInElement in the layout container.
 */
export function createHighchartsResizeObserver(
	getChart: () => Highcharts.Chart | undefined,
	plotContainer: HTMLElement | null | undefined
): ResizeObserver | undefined {
	if (typeof ResizeObserver === 'undefined' || !plotContainer) {
		return undefined;
	}

	// Also observe the GridStack card boundary (.grid-stack-item-content) — this is
	// the element GridStack actually resizes; the inner plot container inherits its size
	// but Highcharts locks its own height, so the inner element alone won't trigger.
	const gridItemContent = plotContainer.closest(
		'.grid-stack-item-content'
	) as HTMLElement | null;

	// Editor Card Preview has no GridStack shell; observe .lce-content so
	// modal/flex settle after first paint still triggers a lightweight reflow.
	const editorPreviewContent = gridItemContent
		? null
		: (plotContainer.closest('.lce-content') as HTMLElement | null);

	const elements = [
		plotContainer,
		gridItemContent,
		editorPreviewContent,
	].filter(
		(el, index, arr): el is HTMLElement => !!el && arr.indexOf(el) === index
	);

	if (elements.length === 0) {
		return undefined;
	}

	let frameId: number | null = null;
	let disconnected = false;

	const cancelScheduledReflow = (): void => {
		if (frameId != null) {
			cancelAnimationFrame(frameId);
			frameId = null;
		}
	};

	const scheduleReflow = (): void => {
		if (disconnected) {
			return;
		}
		if (frameId != null) {
			cancelAnimationFrame(frameId);
		}
		frameId = requestAnimationFrame(() => {
			frameId = null;
			if (disconnected) {
				return;
			}
			// Lightweight reflow only — no chart.update() or legend.render() here.
			// Those DOM-mutating operations run once on resizestop via
			// reflowHighchartsPlotsInElement, avoiding jQuery UI mouse tracking issues.
			safeHighchartsReflow(getChart());
		});
	};

	const observer = new ResizeObserver(scheduleReflow);
	for (const el of elements) {
		observer.observe(el);
	}

	const originalDisconnect = observer.disconnect.bind(observer);
	observer.disconnect = () => {
		disconnected = true;
		cancelScheduledReflow();
		originalDisconnect();
	};

	return observer;
}
